'use client';

import { type Ref, useCallback, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { APP_SETTINGS } from '@/lib/config';
import { CAPTCHA_TOKEN_FIELD } from '@/utils/auth/captcha';

// Without a site key no widget is mounted and the forms submit without a token. That only works
// where GoTrue does not enforce the captcha, which is why the key must be deployed before the
// Supabase-side flag is turned on.
export const CAPTCHA_ENABLED = Boolean(APP_SETTINGS.TURNSTILE_SITE_KEY);

// Turnstile solves in well under a second for an ordinary session; the budget only matters when a
// visitor submits before the challenge has settled, or Cloudflare is unreachable.
const SOLVE_TIMEOUT_MS = 15_000;

export function useCaptcha() {
  const widgetRef = useRef<TurnstileInstance | undefined>(undefined);

  /**
   * Waits for the challenge and puts the token on the form data. Returns false when no token could
   * be obtained, so the caller shows its own copy instead of letting GoTrue reject the request.
   */
  const attachCaptchaToken = useCallback(async (formData: FormData): Promise<boolean> => {
    if (!CAPTCHA_ENABLED) {
      return true;
    }

    try {
      const token = await widgetRef.current?.getResponsePromise(SOLVE_TIMEOUT_MS);
      if (!token) {
        return false;
      }
      formData.set(CAPTCHA_TOKEN_FIELD, token);
      return true;
    } catch {
      return false;
    }
  }, []);

  /**
   * A token is single-use and is spent the moment the action reaches GoTrue, so every failed
   * submit has to hand the widget a fresh challenge before the visitor can retry.
   */
  const resetCaptcha = useCallback(() => {
    widgetRef.current?.reset();
  }, []);

  return { widgetRef, attachCaptchaToken, resetCaptcha };
}

interface CaptchaWidgetProps {
  ref: Ref<TurnstileInstance | undefined>;
}

/**
 * `interaction-only` keeps the widget out of the layout for the vast majority of visitors: it
 * renders nothing until Cloudflare decides a challenge needs solving by hand.
 */
export function CaptchaWidget({ ref }: CaptchaWidgetProps) {
  if (!CAPTCHA_ENABLED) {
    return null;
  }

  return (
    <Turnstile
      ref={ref}
      siteKey={APP_SETTINGS.TURNSTILE_SITE_KEY}
      className="mx-auto"
      options={{
        appearance: 'interaction-only',
        size: 'flexible',
        // The token is attached from the ref at submit time, so the hidden input would be dead
        // weight — and a second, stale copy of the token in the form data.
        responseField: false,
      }}
    />
  );
}
